import { NOTE_IMAGE } from 'constants/note-image';
import { NoteImage, SignedNoteImageUrl } from 'interfaces/note-image.interface';

/**
 * Signed URLs live in memory only and are never persisted or written into a note body
 * (backend docs/note-image-attachments.md §5). Keeping one URL per image until it nears
 * expiry also lets the browser's HTTP cache serve the bytes, which never change for an id.
 */
const signedUrlsByImageId = new Map<string, SignedNoteImageUrl>();

export const storeSignedNoteImageUrl = (image: NoteImage): void => {
  const expiresAtMs = Date.parse(image.url_expires_at);
  if (image.url.length === 0 || Number.isNaN(expiresAtMs)) {
    return;
  }
  signedUrlsByImageId.set(image.id.toLowerCase(), {
    url: image.url,
    expiresAtMs
  });
};

export const getFreshSignedNoteImageUrl = (
  imageId: string,
  nowMs: number = Date.now()
): string | null => {
  const entry = signedUrlsByImageId.get(imageId);
  if (entry == null) {
    return null;
  }
  const isFresh = entry.expiresAtMs - nowMs > NOTE_IMAGE.URL_REFRESH_MARGIN_MS;
  return isFresh ? entry.url : null;
};

export const getSignedNoteImageUrlExpiry = (imageId: string): number | null =>
  signedUrlsByImageId.get(imageId)?.expiresAtMs ?? null;

export const evictSignedNoteImageUrl = (imageId: string): void => {
  signedUrlsByImageId.delete(imageId);
};

export const clearNoteImageUrlCache = (): void => {
  signedUrlsByImageId.clear();
};
